// ============================================
// Chat Page - Full-page AI assistant
// ============================================

import { useState, useRef, useEffect, useCallback } from "react";
import { ChatMessage, DomainItem } from "../../types";
import { GeminiService } from "../../services/geminiService";
import { DropCatchService } from "../../services/dropcatchService";

interface ChatPageProps {
  geminiService: GeminiService;
  dropcatchService: DropCatchService;
  onNavigate?: (view: string) => void;
}

// Quick action templates
const QUICK_ACTIONS = [
  { label: "SUGGEST KEYWORDS", prompt: "Suggest 10 high-value domain keywords I should be searching for" },
  { label: "MARKET TRENDS", prompt: "What are the current market trends for premium domain names?" },
  { label: "BID STRATEGY", prompt: "What's a good bidding strategy for dropped domain auctions?" },
  { label: "ANALYZE TOP PICKS", prompt: "Analyze my top domain picks and suggest which to prioritize" },
];

/**
 * ChatPage Component
 *
 * Full-page AI assistant with:
 * - Multi-turn conversation
 * - Actionable responses (bid, backorder, search)
 * - Quick action templates
 * - Domain context awareness
 */
export function ChatPage({
  geminiService,
  dropcatchService,
  onNavigate: _onNavigate,
}: ChatPageProps) {
  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const [domainContext, setDomainContext] = useState<DomainItem[]>([]);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Add welcome message on mount
  useEffect(() => {
    const welcomeMessage: ChatMessage = {
      id: "welcome",
      role: "model",
      text: `ZERO POINT AI ASSISTANT v2.0 ONLINE

> AVAILABLE COMMANDS:
- DOMAIN VALUATION: "Analyze [domain.com]"
- KEYWORD SEARCH: "Find domains with [keyword]"
- BIDDING STRATEGY: "What should I bid on [domain]?"
- MARKET TRENDS: "What are domain market trends?"
- BATCH ANALYSIS: "Analyze these domains: [list]"

> QUICK TIPS:
- I can search DropCatch for you
- Ask me to suggest keywords
- Request domain valuations
- Get bidding recommendations

TYPE YOUR QUERY BELOW:_`,
      timestamp: Date.now(),
    };
    setMessages([welcomeMessage]);
  }, []);

  // Send message handler
  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: "user",
      text: input.trim(),
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      // Build chat history for context
      const history = messages.map((m) => ({
        role: m.role,
        parts: [{ text: m.text }],
      }));

      // Check for special commands
      const commandResponse = await handleSpecialCommands(input.trim());

      if (commandResponse) {
        const aiMessage: ChatMessage = {
          id: `ai_${Date.now()}`,
          role: "model",
          text: commandResponse,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, aiMessage]);
      } else {
        // Regular chat with AI
        const response = await geminiService.chatMessage(history, input.trim());

        const aiMessage: ChatMessage = {
          id: `ai_${Date.now()}`,
          role: "model",
          text: response,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, aiMessage]);
      }
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        role: "model",
        text: `ERROR: COMMUNICATION FAILURE.
${error instanceof Error ? error.message : "Unknown error"}

Please check your API key configuration and try again.`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, geminiService]);

  // Handle special commands
  const handleSpecialCommands = async (
    command: string
  ): Promise<string | null> => {
    const lowerCommand = command.toLowerCase();

    // Domain analysis command
    if (lowerCommand.startsWith("analyze ")) {
      const domain = command.substring(8).trim();
      if (domain) {
        try {
          const analysis = await geminiService.analyzeDomain(domain, 0);
          return `DOMAIN ANALYSIS: ${domain.toUpperCase()}

VERDICT: ${analysis.verdict}
VALUATION: ${analysis.valuation}

ANALYSIS:
${analysis.analysis}

SEO NOTES:
${analysis.seo_notes}

${analysis.legal_relevance ? `LEGAL RELEVANCE:\n${analysis.legal_relevance}` : ""}

[To bid on this domain, go to the SEARCH page]`;
        } catch (error) {
          return `ERROR: Failed to analyze ${domain}. ${error instanceof Error ? error.message : ""}`;
        }
      }
    }

    // Search command
    if (lowerCommand.startsWith("find ") || lowerCommand.startsWith("search ")) {
      const keyword = command.replace(/^(find|search)\s+/i, "").trim();
      if (keyword) {
        try {
          const results = await dropcatchService.searchAuctions({
            searchTerm: keyword,
            size: 10,
            showAllActive: true,
          });

          if (results.items.length === 0) {
            return `NO RESULTS FOUND FOR "${keyword.toUpperCase()}"

Try:
- Different spelling
- Broader keywords
- Removing filters`;
          }

          // Store domains in context
          setDomainContext(results.items);

          return `SEARCH RESULTS FOR "${keyword.toUpperCase()}"

Found ${results.items.length} domains:

${results.items
  .slice(0, 10)
  .map(
    (d, i) =>
      `${i + 1}. ${d.name}
   Price: $${d.price} | Bidders: ${d.bidders} | Ends: ${new Date(
        d.endTime
      ).toLocaleString()}`
  )
  .join("\n\n")}

[Say "analyze [domain]" to get AI analysis]
[Go to SEARCH page for full results]`;
        } catch (error) {
          return `ERROR: Search failed. ${error instanceof Error ? error.message : ""}`;
        }
      }
    }

    // Suggest keywords command
    if (
      lowerCommand.includes("suggest") &&
      (lowerCommand.includes("keyword") || lowerCommand.includes("domain"))
    ) {
      try {
        const suggestions = await geminiService.suggestDomains([
          "premium",
          "brand",
          "business",
        ]);
        return `SUGGESTED KEYWORDS FOR PREMIUM DOMAINS:

${suggestions.slice(0, 20).join("\n")}

TIPS:
- Combine these with industry-specific terms
- Look for geographic variations (cityname + keyword)
- Check plural and singular forms`;
      } catch (error) {
        return `ERROR: Could not generate suggestions. ${error instanceof Error ? error.message : ""}`;
      }
    }

    // Quick check command
    if (lowerCommand.startsWith("quick ") || lowerCommand.startsWith("check ")) {
      const domain = command.replace(/^(quick|check)\s+/i, "").trim();
      if (domain) {
        try {
          const verdict = await geminiService.quickCheck(domain);
          return `QUICK CHECK: ${domain.toUpperCase()}

${verdict}

[Say "analyze ${domain}" for detailed analysis]`;
        } catch (error) {
          return `ERROR: Quick check failed. ${error instanceof Error ? error.message : ""}`;
        }
      }
    }

    // No special command matched
    return null;
  };

  // Handle quick action click
  const handleQuickAction = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Clear chat
  const handleClear = () => {
    setMessages([]);
    setDomainContext([]);
  };

  // Format timestamp
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  return (
    <div className="h-full flex flex-col bg-black">
      {/* Header */}
      <div className="px-6 py-3 border-b-2 border-green-500 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 animate-pulse" />
            <span className="text-green-400 font-bold tracking-wider">
              AI ASSISTANT
            </span>
          </div>
          <span className="text-green-600 text-sm">GEMINI POWERED</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-green-600">
            {messages.length - 1} MESSAGES
          </span>
          <button
            onClick={handleClear}
            className="px-3 py-1 border border-green-700 text-green-600 text-sm hover:border-green-500 hover:text-green-400"
          >
            [CLR]
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`${
              message.role === "user"
                ? "border-l-4 border-green-500 pl-4"
                : "border-l-4 border-green-700 pl-4"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`text-xs ${
                  message.role === "user" ? "text-green-400" : "text-green-600"
                }`}
              >
                {">"} {message.role === "user" ? "USER" : "SYSTEM"}
              </span>
              <span className="text-xs text-green-800">
                [{formatTime(message.timestamp)}]
              </span>
            </div>
            <pre className="text-green-400 whitespace-pre-wrap font-mono text-sm leading-relaxed">
              {message.text}
            </pre>
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div className="border-l-4 border-green-700 pl-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-green-600">{">"} SYSTEM</span>
            </div>
            <span className="text-green-500 animate-pulse">
              PROCESSING REQUEST...
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      <div className="px-6 py-2 border-t border-green-900">
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-green-700 self-center">QUICK:</span>
          {QUICK_ACTIONS.map((action, i) => (
            <button
              key={i}
              onClick={() => handleQuickAction(action.prompt)}
              className="px-2 py-1 border border-green-800 text-green-600 text-xs hover:border-green-500 hover:text-green-400 transition-colors"
            >
              [{action.label}]
            </button>
          ))}
        </div>
      </div>

      {/* Domain Context (if any) */}
      {domainContext.length > 0 && (
        <div className="px-6 py-2 border-t border-green-900 bg-green-900/20">
          <div className="flex items-center gap-2">
            <span className="text-xs text-green-600">CONTEXT:</span>
            <span className="text-xs text-green-500">
              {domainContext.length} domains loaded
            </span>
            <button
              onClick={() => setDomainContext([])}
              className="text-xs text-green-700 hover:text-green-500"
            >
              [clear]
            </button>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t-2 border-green-500">
        <div className="flex items-center gap-2">
          <span className="text-green-500">{">"}</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter your query..."
            disabled={loading}
            className="flex-1 bg-transparent text-green-400 outline-none placeholder-green-800 font-mono"
          />
          <span
            className={`text-green-500 ${showCursor ? "opacity-100" : "opacity-0"}`}
          >
            _
          </span>
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="px-4 py-2 bg-green-500 text-black font-bold text-sm tracking-wider hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "..." : "SEND"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
